import React, {useEffect, useRef, useState} from "react"
import {createUseStyles, useTheme} from "react-jss"
import {GlobalToken} from "antd/es/theme/interface"
import {ensureServiceHubAuth} from "./serviceHubApi"
import {useProjects} from "./useProjects"
import ProjectAppIcon from "./ProjectAppIcon"
import AllAppsPng from "../../assets/icons/all-apps.png"

const glowEnabled = process.env.REACT_APP_ALL_APPS_GLOW_ENABLED === "true"
const allAppsUrl = process.env.REACT_APP_ALL_APPS_URL || "#"

const useStyles = createUseStyles<string, unknown, GlobalToken>(theme => ({
    "@keyframes allAppsDoubleRipple": {
        "0%":   {boxShadow: "0 0 0 0 rgba(34, 197, 94, 0.6), 0 0 0 0 rgba(34, 197, 94, 0.3)"},
        "70%":  {boxShadow: "0 0 0 8px rgba(34, 197, 94, 0), 0 0 0 14px rgba(34, 197, 94, 0)"},
        "100%": {boxShadow: "0 0 0 0 rgba(34, 197, 94, 0), 0 0 0 0 rgba(34, 197, 94, 0)"},
    },
    wrapper: {
        position: "relative",
    },
    trigger: {
        width: 36,
        height: 36,
        padding: 4,
        background: "transparent",
        border: "none",
        borderRadius: 10,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": {
            background: theme.colorFillTertiary,
        },
        "&:disabled": {
            opacity: 0.6,
            cursor: "not-allowed",
        },
    },
    triggerGlow: {
        animation: "$allAppsDoubleRipple 2s ease-out infinite",
    },
    triggerImg: {},
    dropdown: {
        position: "absolute",
        top: "calc(100% + 8px)",
        right: 0,
        minWidth: 280,
        maxWidth: 360,
        background: theme.colorBgElevated,
        borderRadius: 12,
        padding: 16,
        boxShadow: theme.boxShadowSecondary,
        border: `1px solid ${theme.colorBorderSecondary}`,
        zIndex: 1000,
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.colorTextSecondary,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        marginBottom: 12,
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 8,
    },
    card: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        padding: "8px 4px",
        borderRadius: 8,
        textDecoration: "none",
        "&:hover": {
            background: theme.colorFillTertiary,
            textDecoration: "none",
        },
    },
    cardTitle: {
        fontSize: 11,
        fontWeight: 500,
        color: theme.colorText,
        maxWidth: 80,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        textAlign: "center",
    },
    empty: {
        textAlign: "center",
        color: theme.colorTextSecondary,
        padding: "8px 0",
    },
    footer: {
        marginTop: 12,
        paddingTop: 12,
        borderTop: `1px solid ${theme.colorBorderSecondary}`,
        display: "flex",
        justifyContent: "center",
    },
    footerLink: {
        fontSize: 13,
        fontWeight: 500,
        color: theme.colorText,
        textDecoration: "none",
        padding: "6px 16px",
        borderRadius: theme.borderRadius,
        "&:hover": {
            background: theme.colorFillTertiary,
        },
    },
}))

const ProjectsDropdown: React.FC = () => {
    const theme = useTheme<GlobalToken>()
    const classes = useStyles({theme})
    const [open, setOpen] = useState(false)
    const [authLoading, setAuthLoading] = useState(false)
    const {projects, isLoading, error} = useProjects(open)
    const wrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [open])

    const handleToggle = async () => {
        if (open) {
            setOpen(false)
            return
        }
        setAuthLoading(true)
        try {
            await ensureServiceHubAuth()
            setOpen(true)
        } catch (e) {
            // popup closed or auth failed — do not open
            // eslint-disable-next-line no-console
            console.error("[all-apps] service hub auth failed:", e)
        } finally {
            setAuthLoading(false)
        }
    }

    const handleFooterLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (!process.env.REACT_APP_ALL_APPS_URL) {
            e.preventDefault()
        }
        setOpen(false)
    }

    return (
        <div ref={wrapperRef} className={classes.wrapper}>
            <button
                className={`${classes.trigger}${glowEnabled ? ` ${classes.triggerGlow}` : ""}`}
                onClick={handleToggle}
                disabled={authLoading}
            >
                <img src={AllAppsPng} alt="Все приложения" width={28} height={28} className={classes.triggerImg} />
            </button>

            {open && (
                <div className={classes.dropdown}>
                    {isLoading && <p className={classes.empty}>Загрузка...</p>}

                    {!isLoading && error && (
                        <p className={classes.empty}>Ошибка загрузки: {error}</p>
                    )}

                    {!isLoading && !error && projects.length === 0 && (
                        <p className={classes.empty}>Нет приложений</p>
                    )}

                    {!isLoading && !error && projects.length > 0 && (
                        <>
                            <p className={classes.sectionTitle}>Доступные вам приложения</p>
                            <div className={classes.grid}>
                                {projects.map((project, index) => (
                                    <a
                                        key={project.id}
                                        href={project.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.card}
                                        onClick={() => setOpen(false)}
                                    >
                                        <ProjectAppIcon project={project} index={index} />
                                        <span className={classes.cardTitle}>{project.title}</span>
                                    </a>
                                ))}
                            </div>
                        </>
                    )}

                    {process.env.REACT_APP_ALL_APPS_URL && (
                        <div className={classes.footer}>
                            <a
                                href={allAppsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classes.footerLink}
                                onClick={handleFooterLinkClick}
                            >
                                Все приложения →
                            </a>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default ProjectsDropdown
